import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Button, FormLayout, Heading, Input, Select, Switch } from "@/ui";
import { useForm, useValidator } from "@/hooks";
import styles from "./AeropayForm.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const AeropayCredentialForm = ({
    isCreate,
    handleCancel,
    app,
    stores,
    selectCredential,
    setCredentials,
    isUpdating,
    handlerUpdatingOrCreate,
}) => {
    const [isFormReset, resetFormInputs] = useState(isCreate);
    const { inputs, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "aeropay_store_provider_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Store provider id is required.",
            },
            {
                field: "aeropay_host",
                method: "isEmpty",
                validWhen: false,
                message: "This Host is required.",
            },
            {
                field: "aeropay_api_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Api key is required.",
            },
            {
                field: "aeropay_api_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This Api secret is required.",
            },
            {
                field: "aeropay_merchant_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Merchant id is required.",
            },
            {
                field: "aeropay_location_uuid",
                method: "isEmpty",
                validWhen: false,
                message: "ThisLocation UUID is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const storeOptions = useMemo(
        () =>
            (stores || []).map(store => ({
                value: String(store.value),
                label: store.label,
            })),
        [stores],
    );

    useEffect(() => {
        if (!isCreate) {
            const objSelect = app?.credentials[selectCredential];
            setInputs({
                aeropay_sandbox: objSelect?.aeropay_sandbox,
                aeropay_host: objSelect?.aeropay_host,
                aeropay_api_key: objSelect?.aeropay_api_key,
                aeropay_api_secret: objSelect?.aeropay_api_secret,
                aeropay_merchant_id: objSelect?.aeropay_merchant_id,
                aeropay_store_provider_id: objSelect?.aeropay_store_provider_id,
                aeropay_location_uuid: objSelect?.aeropay_location_uuid,
            });
        }
    }, []);

    if (!isCreate && !isFormReset) {
        resetFormInputs(true);
    }

    const createOrUpdate = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            let accounts = _.cloneDeep(app.credentials ?? []);

            if (isCreate) {
                accounts.push({
                    aeropay_sandbox: inputs?.aeropay_sandbox,
                    aeropay_host: inputs?.aeropay_host,
                    aeropay_api_key: inputs?.aeropay_api_key,
                    aeropay_api_secret: inputs?.aeropay_api_secret,
                    aeropay_merchant_id: inputs?.aeropay_merchant_id,
                    aeropay_location_uuid: inputs?.aeropay_location_uuid,
                    aeropay_store_provider_id:
                        inputs?.aeropay_store_provider_id,
                });
            } else {
                accounts[selectCredential] = inputs;
            }
            setCredentials(accounts);
            handlerUpdatingOrCreate(accounts);
        }
    };

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const handleStoreProviderChange = value => {
        const store = (stores || []).find(s => String(s.value) === value);
        setInputs({
            ...inputs,
            aeropay_store_provider_id: store ? store.value : value,
        });
    };

    return (
        <div className={styles.credentialForm}>
            <Heading level={5}>
                {isCreate ? "Add Credential" : "Update Credential"}
            </Heading>

            <FormLayout>
                <FormLayout.Section title="Credential Informations">
                    <Switch
                        checked={Boolean(inputs.aeropay_sandbox)}
                        label="Sandbox"
                        name="aeropay_sandbox"
                        onChange={inputChange}
                    />
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Select
                            error={fieldError(
                                validator,
                                "aeropay_store_provider_id",
                            )}
                            fullWidth
                            label="Store Provider Id"
                            onValueChange={handleStoreProviderChange}
                            options={storeOptions}
                            value={
                                inputs.aeropay_store_provider_id != null
                                    ? String(inputs.aeropay_store_provider_id)
                                    : undefined
                            }
                        />
                    </FormLayout.Grid>
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Input
                            error={fieldError(validator, "aeropay_api_key")}
                            fullWidth
                            label="Api key"
                            name="aeropay_api_key"
                            onChange={inputChange}
                            placeholder="Api key"
                            value={inputs.aeropay_api_key || ""}
                        />
                        <Input
                            error={fieldError(validator, "aeropay_api_secret")}
                            fullWidth
                            label="Api secret"
                            name="aeropay_api_secret"
                            onChange={inputChange}
                            placeholder="Api secret"
                            value={inputs.aeropay_api_secret || ""}
                        />
                        <Input
                            error={fieldError(validator, "aeropay_merchant_id")}
                            fullWidth
                            label="Merchant id"
                            name="aeropay_merchant_id"
                            onChange={inputChange}
                            placeholder="Merchant id"
                            value={inputs.aeropay_merchant_id || ""}
                        />
                        <Input
                            error={fieldError(validator, "aeropay_host")}
                            fullWidth
                            label="Host"
                            name="aeropay_host"
                            onChange={inputChange}
                            placeholder="Host"
                            value={inputs.aeropay_host || ""}
                        />
                        <Input
                            error={fieldError(
                                validator,
                                "aeropay_location_uuid",
                            )}
                            fullWidth
                            label="Location UUID"
                            name="aeropay_location_uuid"
                            onChange={inputChange}
                            placeholder="Location uid"
                            value={inputs.aeropay_location_uuid || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>
            </FormLayout>

            <div className={styles.credentialActions}>
                <Button
                    disabled={isUpdating}
                    onClick={handleClose}
                    type="button"
                    variant="secondary"
                >
                    Cancel
                </Button>
                <Button
                    loading={isUpdating}
                    onClick={createOrUpdate}
                    type="button"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default AeropayCredentialForm;
